import Vue from 'vue'
import { injectable } from 'inversify'
import axios, {
  AxiosResponse,
  AxiosInstance,
  AxiosError
} from 'axios'
import { IConfig } from '@/config/Config'
import { IModel } from '@/model/model'
import TYPES from '@/config/Types'
import { DependencyContainer } from '@/config/DependencyContainer'
import { AuthHandler } from '@/service/AuthHandler'
import axiosRetry from 'axios-retry'

export abstract class ApiService {
  abstract getAny<T> (url: string): Promise<T>
  abstract get<T extends IModel[]> (url: string): Promise<T>
  abstract getSingle<T extends IModel> (url: string): Promise<T>
  abstract search<T extends IModel[]> (url: string, params: any): Promise<T>
  abstract post<T extends IModel> (url: string, params: any): Promise<T>
  abstract put<T extends IModel> (url: string, id: number, params: any): Promise<T>
  abstract delete<T extends IModel> (url: string, id: number): Promise<T>
  abstract deleteSub<T extends IModel> (url: string, id: number, subName: string): Promise<T>
  abstract deleteWithParams<T extends IModel> (url: string, params: any): Promise<T>
}

@injectable()
export class ApiServiceImpl implements ApiService {
  private config : IConfig | null | undefined
  private axiosInstance: AxiosInstance | null | undefined
  private authHandler: AuthHandler | null | undefined

  constructor () {
    this.config = DependencyContainer.getContainer().get<IConfig>(TYPES.IConfig)
    this.authHandler = DependencyContainer.getContainer().get<AuthHandler>(TYPES.AuthHandler)
    this.axiosInstance = axios.create({
      baseURL: this.config ? this.config.getApiUrl() : 'error'
    })
  }

  public getAny<T> (url: string): Promise<T> {
    return this.getInternal<T>(url)
  }

  public get<T extends IModel[]> (url: string): Promise<T> {
    return this.getInternal<T>(url)
  }

  public getSingle<T extends IModel> (url: string): Promise<T> {
    return this.getInternal<T>(url)
  }

  public search<T extends IModel[]> (url: string, params: any): Promise<T> {
    return this.sendRequest<T>('post', url, params)
  }

  public post<T extends IModel> (url: string, params: any): Promise<T> {
    return this.sendRequest<T>('post', url, params)
  }

  public put<T extends IModel> (url: string, id: number, params: any): Promise<T> {
    return this.sendRequest<T>('put', `${ url }/${ id }/`, params)
  }

  public delete<T extends IModel> (url: string, id: number): Promise<T> {
    return this.sendRequest<T>('delete', `${ url }/${ id }/`, { })
  }

  public deleteSub<T extends IModel> (url: string, id: number, subName: string): Promise<T> {
    return this.sendRequest<T>('delete', `${ url }/${ id }/${ subName }/`, { })
  }

  public deleteWithParams<T extends IModel> (url: string, params: any): Promise<T> {
    return this.sendRequest<T>('delete', url, params)
  }

  private getAuthHeader () {
    console.log('jwt token: %j', this.getAuthHandler().getToken())
    if (this.getAuthHandler().getToken()) {
      return { Authorization : `Bearer ${ this.getAuthHandler().getToken() }` }
    }
  }

  private sendRequest<T> (method: string, url: string, params: any): Promise<T> {
    let header = this.getAuthHeader()
    console.log('header: %j', header)
    return new Promise<T>((resolve, reject) => {
      axiosRetry(this.getAxiosInstance(), { retryDelay: (<any>axiosRetry).exponentialDelay});
      this.getAxiosInstance().request({
        method: method,
        url: url,
        data: params,
        headers: header
      }).then((resp: AxiosResponse<T>) => {
        console.log('get: response = %j', resp)
        console.log('get: data = %j', resp.data)
        return resolve(resp.data)
      }).catch((err: AxiosError) => {
        console.log('sendRequest: error: %j', err)
        return reject(err)
      })
    })
  }

  private getInternal<T> (url: string): Promise<T> {
    return this.sendRequest<T>('get', url, { })
  }

  public getAxiosInstance (): any {
    if (this.axiosInstance) return this.axiosInstance
    throw ('axiosInstance is null')
  }

  public getAuthHandler (): AuthHandler {
    if (this.authHandler) return this.authHandler
    throw ('authHandler is null')
  }
}
